import { css } from "@emotion/react";
import { showPrice } from "@swo/shared";
import { FullTextSearch, SwarovskiOptikLocaleFrontend } from "@swo/storefront";
import React from "react";
import { PlayIconWithWrapper } from "../../../components/StoriesOverviewComponent/Videos/FocalImageWithPlayButton";
import { interpolateLinearly } from "../../../domain/translations/simple-translations-module";
import { usePageContext } from "../../../global/page-context";
import { FONTS, fontColor, fontSize } from "../../../styles/fonts";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { NoSearchResultsParagraph } from "./no-search-results-paragraph";
import { SearchT } from "./search-component";
import { SearchResultsDownloadsViewSection } from "./search-results-downloads-view-section";
import { elementIDs } from "./search-results-ids";
import { SearchResultsVisualViewSection } from "./search-results-visual-view-section";
import { mapTranslatedValue, T } from "../../../components/T";

export const SearchResultsView: ReactTFC<{
  searchResult: FullTextSearch;
  query: string;
  locale: SwarovskiOptikLocaleFrontend;
  totalCount: number;
  t: SearchT;
}> = ({ t, ...props }) => {
  const ctx = usePageContext();

  const hasAnyResults = props.totalCount > 0;

  return hasAnyResults ? (
    <React.Fragment>
      <SearchResultsVisualViewSection
        id={elementIDs.products}
        headline={mapTranslatedValue(t("section.products"), _ => `${props.searchResult.products.length} ${_}`)}
        tiles={props.searchResult.products.map(p => {
          const maybeImage = p.image
            ? { url: p.image.url, focalPoint: undefined, sys: { id: p.image.sys.id } }
            : undefined;

          const maybePrice = p.lowestPrice;

          return {
            key: p.__key,
            image: maybeImage,
            headline: p.name,
            targetUrl: p.link,
            subheadline: undefined,
            secondaryLabel: maybePrice
              ? mapTranslatedValue(
                  ctx.generalTranslations("fromPriceLabel"),
                  _ => `${_} ${showPrice(maybePrice, props.locale)}`,
                )
              : undefined,
          };
        })}
        seeMoreLabel={mapTranslatedValue(
          t("section.products.seeAll"),
          _ => `${_} (${props.searchResult.products.length})`,
        )}
        NoSearchResultsFound={
          <NoSearchResultsParagraph
            text={
              <T forcedValue={t("section.products.noResults")}>
                {interpolateLinearly(t("section.products.noResults").value, [props.query])}
              </T>
            }
          />
        }
      />

      <SearchResultsVisualViewSection
        id={elementIDs.videoStories}
        headline={mapTranslatedValue(t("section.videos"), _ => `${props.searchResult.videoStories.length} ${_}`)}
        tiles={props.searchResult.videoStories.map(story => {
          return {
            key: story.link || "#",
            image: {
              sys: undefined,
              url: story.image.url,
              title: story.image.title,
              width: story.image.width,
              height: story.image.height,
              focalPoint: story.image.focalPoint || { x: story.image.width / 2, y: story.image.height / 2 },
            },
            headline: story.emotionalHeadline,
            targetUrl: story.link || "#",
            subheadline: story.title,
            secondaryLabel: story.tags.join(" | "),
          };
        })}
        seeMoreLabel={mapTranslatedValue(
          t("section.videos.seeAll"),
          _ => `${_} (${props.searchResult.videoStories.length})`,
        )}
        NoSearchResultsFound={
          <NoSearchResultsParagraph
            text={
              <T forcedValue={t("section.videos.noResults")}>
                {interpolateLinearly(t("section.videos.noResults").value, [props.query])}
              </T>
            }
          />
        }
        Overlay={
          <PlayIconWithWrapper
            css={css`
              z-index: 1;
              pointer-events: none;
            `}
          />
        }
      />

      <SearchResultsVisualViewSection
        id={elementIDs.stories}
        headline={mapTranslatedValue(t("section.stories"), _ => `${props.searchResult.stories.length} ${_}`)}
        tiles={props.searchResult.stories.map(story => {
          return {
            key: story.link || "#",
            image: {
              sys: undefined,
              url: story.image.url,
              title: story.image.title,
              width: story.image.width,
              height: story.image.height,
              focalPoint: story.image.focalPoint,
            },
            headline: story.emotionalHeadline,
            targetUrl: story.link || "#",
            subheadline: story.title,
            secondaryLabel: story.tags.join(" | "),
          };
        })}
        seeMoreLabel={mapTranslatedValue(
          t("section.stories.seeAll"),
          _ => `${_} (${props.searchResult.stories.length})`,
        )}
        NoSearchResultsFound={
          <NoSearchResultsParagraph
            text={
              <T forcedValue={t("section.stories.noResults")}>
                {interpolateLinearly(t("section.stories.noResults").value, [props.query])}
              </T>
            }
          />
        }
      />

      <SearchResultsDownloadsViewSection
        id={elementIDs.downloads}
        headline={mapTranslatedValue(t("section.downloads"), _ => `${props.searchResult.downloads.length} ${_}`)}
        downloads={props.searchResult.downloads}
        t={t}
        NoSearchResultsFound={
          <NoSearchResultsParagraph
            text={
              <T forcedValue={t("section.downloads.noResults")}>
                {interpolateLinearly(t("section.downloads.noResults").value, [props.query])}
              </T>
            }
          />
        }
      />
    </React.Fragment>
  ) : (
    <p
      css={[
        FONTS.SwarovskiFutura300,
        fontColor.black,
        fontSize.x2,
        css`
          text-align: center;

          grid-row: 2 / span 3;
          align-self: center;
        `,
      ]}
    >
      <T forcedValue={t("noResults")}>{interpolateLinearly(t("noResults").value, [props.query])}</T>
    </p>
  );
};
